import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Vehicle,
  formatMileage,
  formatPrice,
  getVehicleImage,
} from '../../components/website/WebsiteTemplate';
import VehicleContactForm from '../../components/public/VehicleContactForm';
import { apiService } from '../../services/api';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { usePublicSitePath } from './publicSitePaths';
import { VehicleDetailCopy } from './types';

interface VehicleDetailPageProps {
  vehicles: Vehicle[];
  labels: VehicleDetailCopy;
  contactPhone: string;
  bhphBadgeImageUrl?: string;
}

const VehicleDetailPage: React.FC<VehicleDetailPageProps> = ({
  vehicles,
  labels,
  contactPhone,
  bhphBadgeImageUrl,
}) => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const publicSitePath = usePublicSitePath();
  const badgeImageUrl = bhphBadgeImageUrl ? apiService.getFileUrl(bhphBadgeImageUrl) : undefined;
  const vehicle = vehicles.find((item) => item.id === vehicleId || item.vin === vehicleId);
  const images = useMemo(() => {
    if (!vehicle) return [];
    const fromArray = (vehicle.images || []).filter(
      (url): url is string => typeof url === 'string' && url.length > 0
    );
    const all = [vehicle.image_url, ...fromArray].filter(
      (url): url is string => typeof url === 'string' && url.length > 0
    );
    const unique = Array.from(new Set(all));
    return unique.length > 0 ? unique : ['/images/vehicle_placeholder.svg'];
  }, [vehicle]);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const thumbsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (images.length > 0) setSelectedIdx(0);
  }, [images]);

  // Scroll active thumbnail into view
  useEffect(() => {
    if (thumbsRef.current) {
      const active = thumbsRef.current.children[selectedIdx] as HTMLElement | undefined;
      active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedIdx]);

  if (!vehicle) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <p className="text-gray-800 mb-6">{labels.notFound}</p>
          <Link
            to={publicSitePath('/inventory')}
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            {labels.backToInventory}
          </Link>
        </div>
      </main>
    );
  }

  const dialHref = `tel:${contactPhone.replace(/[^0-9]/g, '')}`;
  const vehicleLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const selectedImage = images[selectedIdx] || getVehicleImage(vehicle);

  const specs = [
    { label: labels.vin, value: vehicle.vin },
    { label: labels.mileage, value: formatMileage(vehicle.mileage) },
    { label: labels.transmission, value: vehicle.transmission },
    { label: labels.fuel, value: vehicle.fuel_type },
    { label: labels.color, value: vehicle.exterior_color },
    { label: labels.body, value: vehicle.body_style },
  ].filter((s) => s.value);

  return (
    <main className="bg-gray-50 pb-24 md:pb-8">
      {/* Back link */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-2">
        <Link
          to={publicSitePath('/inventory')}
          className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900"
        >
          ← {labels.backToInventory}
        </Link>
      </div>

      {/* ── Full-width image gallery ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
        <div className="bg-black rounded-xl overflow-hidden">
          {/* Main image */}
          <div className="relative" style={{ paddingBottom: '56.25%' }}>
            <img
              src={selectedImage}
              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
              className="absolute inset-0 w-full h-full object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/images/vehicle_placeholder.svg';
              }}
            />
            {/* Photo counter */}
            {images.length > 1 && (
              <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                {selectedIdx + 1} / {images.length}
              </span>
            )}
            {/* Prev/Next arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedIdx((i) => (i - 1 + images.length) % images.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                  aria-label="Previous"
                >
                  ‹
                </button>
                <button
                  onClick={() => setSelectedIdx((i) => (i + 1) % images.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                  aria-label="Next"
                >
                  ›
                </button>
              </>
            )}
          </div>

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div
              ref={thumbsRef}
              className="flex gap-2 p-2 overflow-x-auto bg-gray-900"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' }}
            >
              {images.map((url, idx) => (
                <button
                  key={url}
                  onClick={() => setSelectedIdx(idx)}
                  className={`flex-shrink-0 rounded overflow-hidden border-2 transition-all ${
                    idx === selectedIdx
                      ? 'border-yellow-400 opacity-100'
                      : 'border-transparent opacity-60 hover:opacity-90'
                  }`}
                  style={{ width: 80, height: 56 }}
                >
                  <img
                    src={url}
                    alt={`Photo ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/vehicle_placeholder.svg';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Main content area ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: 2/3 — specs + description */}
          <div className="lg:col-span-2 space-y-5">
            {/* Vehicle title */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
              <h1
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-1"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              {vehicle.trim && <p className="text-gray-500 text-sm">{vehicle.trim}</p>}
              {/* Mobile price (visible below lg) */}
              <div className="lg:hidden mt-3 pt-3 border-t border-gray-100">
                {vehicle.bhph_eligible && vehicle.bhph_min_down_value != null ? (
                  <div className="flex items-center gap-2">
                    {badgeImageUrl && (
                      <img
                        src={badgeImageUrl}
                        alt="In-House Financing"
                        className="h-8 w-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    <div>
                      <p className="text-2xl font-bold text-blue-700 leading-none">
                        {vehicle.bhph_min_down_type === 'percent'
                          ? `${vehicle.bhph_min_down_value}%`
                          : `$${vehicle.bhph_min_down_value.toLocaleString()}`}
                      </p>
                      <p className="text-xs text-gray-500">Down Payment</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-3xl font-bold text-blue-700">{formatPrice(vehicle.price)}</p>
                )}
              </div>
            </div>

            {/* Specs grid */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                Vehicle Specs
              </h2>
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4 text-sm">
                {specs.map(({ label, value }) => (
                  <div key={label} className="border-b border-gray-100 pb-3">
                    <dt className="text-gray-400 text-xs uppercase tracking-wide mb-0.5">
                      {label}
                    </dt>
                    <dd className="text-gray-900 font-semibold">{value || labels.na}</dd>
                  </div>
                ))}
              </dl>
            </div>

            {/* Description */}
            {vehicle.description && (
              <div className="bg-white rounded-xl border border-gray-200 p-5 md:p-6">
                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-4">
                  About This Vehicle
                </h2>
                <div
                  className={`prose prose-sm max-w-none text-gray-700 leading-relaxed overflow-hidden transition-all duration-300 ${
                    descExpanded ? '' : 'max-h-40'
                  }`}
                  // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml -- content is sanitized via DOMPurify (sanitizeHtml)
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(vehicle.description) }}
                />
                <button
                  onClick={() => setDescExpanded((e) => !e)}
                  className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-semibold"
                >
                  {descExpanded ? '▲ Show less' : '▼ Read full description'}
                </button>
              </div>
            )}
          </div>

          {/* Right: 1/3 — Price + CTAs (sticky on desktop) */}
          <div className="hidden lg:block">
            <div className="sticky top-4 space-y-4">
              {/* Price card */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                {vehicle.bhph_eligible && vehicle.bhph_min_down_value != null ? (
                  <div className="mb-5">
                    <div className="flex items-center gap-3 mb-1">
                      {badgeImageUrl && (
                        <img
                          src={badgeImageUrl}
                          alt="In-House Financing"
                          className="h-10 w-auto"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <p className="text-4xl font-bold text-blue-700">
                        {vehicle.bhph_min_down_type === 'percent'
                          ? `${vehicle.bhph_min_down_value}%`
                          : `$${vehicle.bhph_min_down_value.toLocaleString()}`}
                      </p>
                    </div>
                    <p className="text-sm text-gray-500">Down Payment</p>
                  </div>
                ) : (
                  <p className="text-4xl font-bold text-blue-700 mb-5">
                    {formatPrice(vehicle.price)}
                  </p>
                )}

                <div className="space-y-3">
                  <a
                    href={dialHref}
                    className="flex items-center justify-center gap-2 w-full rounded-xl bg-red-600 hover:bg-red-700 text-white py-3.5 font-bold text-base transition-colors"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    📞 {labels.ctaCall}
                  </a>
                  <Link
                    to={publicSitePath(`/financing?vehicleId=${encodeURIComponent(vehicle.id)}`)}
                    className="flex items-center justify-center gap-2 w-full rounded-xl py-3.5 font-bold text-base transition-colors text-gray-900"
                    style={{ backgroundColor: '#FACC15', fontFamily: "'Oswald', sans-serif" }}
                  >
                    📋 {labels.ctaApply}
                  </Link>
                </div>

                <p className="text-xs text-gray-400 mt-4 leading-relaxed">{labels.disclaimer}</p>
              </div>

              {/* BHPH info card */}
              {vehicle.bhph_eligible ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">
                    In-House Financing Available (BHPH)
                  </h3>
                  <div className="flex items-baseline mb-3 pb-3 border-b border-green-200">
                    <span className="text-green-600 font-medium text-sm">Cash Price:</span>
                    <span className="ml-1 text-gray-900 font-bold">
                      {formatPrice(vehicle.price)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {vehicle.bhph_payment_amount && (
                      <div>
                        <span className="text-green-600 font-medium">Payment:</span>
                        <span className="ml-1 text-gray-900">
                          ${vehicle.bhph_payment_amount.toLocaleString()}/
                          {vehicle.bhph_payment_frequency === 'biweekly' ? 'bi-weekly' : 'mo'}
                        </span>
                      </div>
                    )}
                    {vehicle.bhph_min_down_value != null && (
                      <div>
                        <span className="text-green-600 font-medium">Min Down:</span>
                        <span className="ml-1 text-gray-900">
                          {vehicle.bhph_min_down_type === 'percent'
                            ? `${vehicle.bhph_min_down_value}%`
                            : `$${vehicle.bhph_min_down_value.toLocaleString()}`}
                        </span>
                      </div>
                    )}
                    {vehicle.bhph_term_months && (
                      <div>
                        <span className="text-green-600 font-medium">Term:</span>
                        <span className="ml-1 text-gray-900">
                          {vehicle.bhph_term_months} months
                        </span>
                      </div>
                    )}
                  </div>
                  <a
                    href={publicSitePath('/financing')}
                    className="inline-block mt-3 px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
                  >
                    Apply for Financing
                  </a>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h3 className="text-base font-semibold text-blue-800">Financing Available</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    This vehicle qualifies for financing through our lending partners.
                  </p>
                  <a
                    href={publicSitePath('/financing')}
                    className="inline-block mt-3 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Apply for Financing
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Always-visible inline lead-capture form — same on every device, no channel handoff */}
        <div className="mt-5">
          <VehicleContactForm
            vehicleId={vehicle.id}
            vehicleLabel={vehicleLabel}
            dealerPhone={contactPhone}
            labels={labels}
          />
        </div>

        <div className="lg:hidden mt-5 grid grid-cols-2 gap-3">
          <a
            href={dialHref}
            className="flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 text-white py-3.5 font-bold text-sm transition-colors"
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            📞 {labels.ctaCall}
          </a>
          <Link
            to={publicSitePath(`/financing?vehicleId=${encodeURIComponent(vehicle.id)}`)}
            className="flex items-center justify-center gap-2 rounded-xl py-3.5 font-bold text-sm transition-colors text-gray-900"
            style={{ backgroundColor: '#FACC15', fontFamily: "'Oswald', sans-serif" }}
          >
            📋 {labels.ctaApply}
          </Link>
        </div>
      </div>
    </main>
  );
};

export default VehicleDetailPage;
